import { Dialog } from "@dash/dialog";
import { Button, TextField } from "@dash/form";
import { useTheme } from "@emotion/react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import { MdOutlineEdit } from "react-icons/md";
import { Link } from "react-router-dom-v5-compat";

import { CustomerDoc } from "@megaron/docs-contracts";
import { getMegaraxPath } from "@megaron/megarax-uri";

type Props = {
  reporterName: string;
  reporterEmail: string;
  reporterPhone: string | null;
  emailMatchedCustomer?: CustomerDoc;
  isEditable?: boolean;
  isSaving?: boolean;
  onSave?: (values: { reporterName: string; reporterEmail: string; reporterPhone: string }) => void;
};

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export const SupportTicketManualReporterCard: FC<Props> = ({
  reporterName,
  reporterEmail,
  reporterPhone,
  emailMatchedCustomer,
  isEditable,
  isSaving,
  onSave,
}) => {
  const theme = useTheme();
  const trimmedName = reporterName.trim();
  const trimmedEmail = reporterEmail.trim();
  const trimmedPhone = reporterPhone?.trim() ?? "";
  const displayName = trimmedName || (trimmedEmail ? trimmedEmail.split("@")[0] : "—");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [nameDraft, setNameDraft] = useState(trimmedName);
  const [emailDraft, setEmailDraft] = useState(trimmedEmail);
  const [phoneDraft, setPhoneDraft] = useState(trimmedPhone);

  useEffect(() => {
    setNameDraft(reporterName.trim());
    setEmailDraft(reporterEmail.trim());
    setPhoneDraft(reporterPhone?.trim() ?? "");
  }, [reporterName, reporterEmail, reporterPhone]);

  const canEdit = !!isEditable && !!onSave;
  const emailError = emailDraft.trim() && !isValidEmail(emailDraft.trim()) ? "Nieprawidłowy adres e-mail" : null;
  const canSubmit = !emailError && !isSaving;

  const closeDialog = () => {
    setNameDraft(trimmedName);
    setEmailDraft(trimmedEmail);
    setPhoneDraft(trimmedPhone);
    setIsDialogOpen(false);
  };

  const handleSubmit = () => {
    if (!canSubmit || !onSave) return;
    onSave({
      reporterName: nameDraft.trim(),
      reporterEmail: emailDraft.trim().toLowerCase(),
      reporterPhone: phoneDraft.trim(),
    });
    setIsDialogOpen(false);
  };

  return (
    <>
      <div
        css={{
          borderRadius: theme.smallBorderRadius,
          background: theme.colors.primaryLight,
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
          border: `2px solid ${theme.colors.primary}`,
        }}
      >
        <div
          css={{
            minWidth: 0,
            padding: "0.5rem 0.75rem",
            display: "flex",
            flex: "1 0 0",
            flexDirection: "column",
            gap: "0.25rem",
          }}
        >
          <div css={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "0.5rem" }}>
            <div css={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
              <span css={{ fontSize: "0.75rem", fontWeight: 600, color: theme.colors.primary }}>
                Zgłaszający · wpis ręczny
              </span>
              <span css={{ fontSize: "0.875rem", fontWeight: 700, color: theme.colors.primary }}>{displayName}</span>
            </div>
            <div css={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
              <div css={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px" }}>
                {trimmedPhone ? (
                  <span css={{ fontSize: "0.75rem", color: theme.colors.primary }}>{trimmedPhone}</span>
                ) : null}
                {trimmedEmail ? (
                  <span css={{ fontSize: "0.75rem", color: theme.colors.primary, textAlign: "right" }}>
                    {trimmedEmail}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
          {emailMatchedCustomer ? (
            <span css={{ fontSize: "0.75rem", color: theme.colors.secondaryText }}>
              W CRM znaleziono klienta o tym e-mailu:{" "}
              <Link
                to={getMegaraxPath.uri("megarax://crm/customers/id/{id}", { id: emailMatchedCustomer.uuid })}
                css={{ color: theme.colors.primary, fontWeight: 600 }}
              >
                {emailMatchedCustomer.name}
              </Link>
            </span>
          ) : null}
        </div>
        {canEdit ? (
          <button
            type="button"
            onClick={() => setIsDialogOpen(true)}
            title="Edytuj kontakt zgłaszającego"
            css={{
              color: theme.colors.primary,
              background: "white",
              border: "none",
              borderLeft: `1px solid ${theme.colors.primary}`,
              padding: "0.5rem 0.875rem",
              alignSelf: "stretch",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { opacity: 0.85 },
            }}
          >
            <MdOutlineEdit size={18} />
          </button>
        ) : null}
      </div>
      {isDialogOpen ? (
        <Dialog
          header="Kontakt zgłaszającego"
          onClose={closeDialog}
          placement="center"
          contentCss={{ width: "420px", maxWidth: "calc(100% - 4rem)" }}
        >
          <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem", width: "100%" }}>
            <TextField label="Imię i nazwisko" value={nameDraft} onChange={setNameDraft} isDisabled={isSaving} />
            <TextField
              label="E-mail"
              value={emailDraft}
              onChange={setEmailDraft}
              isDisabled={isSaving}
              error={emailError}
            />
            <TextField label="Telefon" value={phoneDraft} onChange={setPhoneDraft} isDisabled={isSaving} />
            <div css={{ display: "flex", gap: "0.5rem", width: "100%" }}>
              <Button css={{ width: "100%" }} variant="outline" onClick={closeDialog} isDisabled={isSaving}>
                Anuluj
              </Button>
              <Button css={{ width: "100%" }} onClick={handleSubmit} isDisabled={!canSubmit}>
                Zapisz
              </Button>
            </div>
          </div>
        </Dialog>
      ) : null}
    </>
  );
};
